import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Monitor, 
  HardDrive, 
  Shield, 
  Thermometer,
  Wrench,
  Database,
  Laptop,
  Settings,
  Heart,
  CheckCircle2,
  Users
} from "lucide-react";
import SEO, { pageSEO } from "../components/SEO";
import MainLayout from "../components/MainLayout";

const AboutSmartITPage = () => {
  const services = [
    { icon: Monitor, text: "Laptop and PC diagnostics" },
    { icon: HardDrive, text: "SSD upgrades and speed improvements" },
    { icon: Laptop, text: "Screen replacement" },
    { icon: Database, text: "Data recovery" },
    { icon: Shield, text: "Virus and malware removal" },
    { icon: Thermometer, text: "Overheating and internal cleaning" },
    { icon: Wrench, text: "Thermal paste replacement" },
    { icon: Settings, text: "Software troubleshooting" },
    { icon: Users, text: "Small IT support for home and office" },
  ];

  return (
    <MainLayout>
      <div data-testid="about-smartit-page">
        <SEO {...pageSEO.aboutSmartit} />
        {/* Header */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                About Smart<span className="text-blue-500">IT</span>
              </h1>
            </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-12"
        >
          <p className="text-slate-300 text-lg leading-relaxed">
            SmartIT is a professional laptop and PC repair service based in Aylesbury, 
            offering fast, reliable and affordable technical support for home users and 
            small businesses. My mission is to make computer repairs simple, transparent 
            and stress-free for everyone.
          </p>
        </motion.div>

        {/* Services Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-xl font-semibold text-white mb-6">
            I provide a complete range of services, including:
          </h2>
          
          <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl border border-slate-700/50 p-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <motion.div
                  key={service.text}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{service.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Free Diagnostic Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/20 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Free Test & Diagnostic
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Every customer receives <span className="text-blue-400 font-medium">Free Test & Diagnostic</span> before 
                  any repair. This means you always know the exact problem and the exact cost 
                  before deciding anything.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="border-t border-slate-800/50 my-12"></div>

        {/* Community & Volunteering Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 border border-pink-500/30 flex items-center justify-center">
              <Heart className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              Community & Volunteering
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl border border-slate-700/50 p-6">
            <p className="text-slate-300 leading-relaxed mb-4">
              In my free time, I volunteer within the community, including support activities 
              for the RSPCA. I enjoy helping where it's needed and contributing to animal 
              welfare and local initiatives.
            </p>
            <p className="text-slate-400 leading-relaxed italic">
              This reflects the values behind SmartIT: <span className="text-white not-italic font-medium">responsibility</span>, <span className="text-white not-italic font-medium">honesty</span> and <span className="text-white not-italic font-medium">care</span>.
            </p>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            Ready to get your device fixed?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/diagnostic"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity"
              data-testid="about-diagnostic-btn"
            >
              <Monitor className="w-5 h-5" />
              Run Free Diagnostic
            </Link>
            <a
              href="https://wa.me/447435324947"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-green-600 transition-colors"
              data-testid="about-whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Contact on WhatsApp
            </a>
          </div>
        </motion.div>
      </div>

      </div>
    </MainLayout>
  );
};

export default AboutSmartITPage;
